﻿import device from "current-device";
import { getTranslations } from "../translations";

export enum spinnerType
{
    default = "default",
    smulders = "smulders",
    alternative = "alt"
}

export enum spinnerState
{
    none = "",
    absoluteTop = "u-progress-spinner--absolute-top",
    absoluteBottom = "u-progress-spinner--absolute-bottom",
    fixed = "u-progress-spinner--fixed"
}

export class progressSpinner
{
    input: HTMLElement | string;
    container: HTMLElement;
    root: HTMLElement;
    spinnerIcon: spinnerType;
    loaderIcon: HTMLElement;
    loadIconState: spinnerState;
    usedOverlay: boolean;
    private lastActiveElement: HTMLElement | null = null;

    constructor(input: HTMLElement | string, spinnerIcon: spinnerType)
    {
        const instance = this;
        this.input = input;
        if (typeof input === "string")
            this.container = $(input)[0];
        else
            this.container = input;

        this.spinnerIcon = spinnerIcon;

        this.build();

        // When page return, hide loader
        window.addEventListener("pageshow", function (event) {
            const [entry] = window.performance.getEntriesByType("navigation");

            if (entry.entryType === "back_forward" || event.persisted) {
                instance.hide();
            }
        });
    }


    private build()
    {
        // Check if container already has a spinner
        if (this.container.querySelector(".u-progress-spinner"))
            this.container.querySelector(".u-progress-spinner").remove();

        const progressSpinnerHolder = document.createElement("div");
        progressSpinnerHolder.className = "u-progress-spinner";

        // label for screenreaders
        progressSpinnerHolder.setAttribute("aria-label", getTranslations().txtPageLoading);

        const progressSpinner = document.createElement("img") as HTMLImageElement;

        switch (this.spinnerIcon)
        {
            case spinnerType.smulders:
                {
                    progressSpinner.className = "u-progress-spinner__loader u-progress-spinner__loader--smulders";
                    break;
                }
            case spinnerType.alternative:
                {
                    progressSpinner.className = "u-progress-spinner__loader u-progress-spinner__loader--alt";
                    break;
                }
            case spinnerType.default:
            default: {
                progressSpinner.className = "u-progress-spinner__loader";
                break;
            }
        }

        progressSpinner.width = 50;
        progressSpinner.height = 50;

        progressSpinner.src = `${shopConfig().staticDomain}/designimages/smulders/loading-smulders.svg?v=${shopConfig().versionNumber}`;

        // Hide image for screenreaders
        progressSpinner.alt = "";
        progressSpinner.setAttribute("aria-hidden", "true");

        progressSpinner.loading = "lazy";

        this.loaderIcon = progressSpinner;

        progressSpinnerHolder.appendChild(progressSpinner);
        this.root = progressSpinnerHolder;
        this.container.prepend(progressSpinnerHolder);

        this.checkPosition();
    }

    private checkPosition()
    {
        const instance = this;
        // ResizeObserver for viewport changes
        const resizeObserver = new ResizeObserver(() =>
        {
            instance.setLoadIconPosition(true);
        });

        resizeObserver.observe(document.documentElement);

        // Scroll event
        window.addEventListener("scroll", () => { instance.setLoadIconPosition(false); }, { passive: true });

        // Load event
        window.addEventListener("load", () => { instance.setLoadIconPosition(false); });
    }

    private setLoadIconPosition = (recalculatePosition) =>
    {
        if (recalculatePosition)
            this.loadIconState = spinnerState.none;

        if (this.root)
        {
            if (device.mobile())
            {
                $(this.root).addClass(spinnerState.fixed);
            }
            else
            {
                var contentOffsetTop = $(this.root).parent()?.offset()?.top;
                var footerOffsetTop = $(".w-footer").offset().top;

                // Minimum distance between the load icon and the start of the content area.
                var loaderOffsetToHeader = 47;

                // Static number is the minimum distance between the load icon and the footer.
                var loaderOffsetToFooter = $(this.loaderIcon).height() + 32;

                var filterOverlayOffset = parseInt($(this.root).css("margin-top"));

                if (isNaN(filterOverlayOffset))
                    filterOverlayOffset = 0;

                var scrollPos = $(window).scrollTop();
                var scrollStart = contentOffsetTop - ($(window).height() / 2) + loaderOffsetToHeader + filterOverlayOffset;
                var scrollStop = footerOffsetTop - ($(window).height() / 2) - loaderOffsetToFooter;

                // Set to relative when load icon is not beyond the scroll start threshold.
                if (this.loadIconState !== spinnerState.absoluteTop && scrollPos < scrollStart)
                {
                    this.loadIconState = spinnerState.absoluteTop;

                    $(this.root).addClass(spinnerState.absoluteTop);
                    $(this.root).removeClass(spinnerState.fixed);
                    $(this.root).removeClass(spinnerState.absoluteBottom);

                    $(this.loaderIcon).css("top", "");
                }
                // Fix load icon to center of screen when between the scroll start and end thresholds.
                else if (this.loadIconState !== spinnerState.fixed && scrollPos >= scrollStart && scrollPos < scrollStop)
                {
                    this.loadIconState = spinnerState.fixed;

                    $(this.root).removeClass(spinnerState.absoluteTop);
                    $(this.root).addClass(spinnerState.fixed);
                    $(this.root).removeClass(spinnerState.absoluteBottom);

                    $(this.loaderIcon).css("top", "");
                }
                // Set loadicon to a fixed position right above the footer (using absolute) when beyond the scroll end threshold.
                else if (this.loadIconState != spinnerState.absoluteBottom && scrollPos > scrollStop)
                {
                    this.loadIconState = spinnerState.absoluteBottom

                    $(this.root).removeClass(spinnerState.absoluteTop);
                    $(this.root).removeClass(spinnerState.fixed);
                    $(this.root).addClass(spinnerState.absoluteBottom);

                    $(this.loaderIcon).css("top", $(this.root).height() - loaderOffsetToFooter - filterOverlayOffset);
                }
            }
        }
    }

    public show(overlay: boolean)
    {
        if (!this.exists())
        {
            if (typeof this.input === "string")
                this.container = $(this.input)[0];
            else
                this.container = this.input;

            this.build();
        }

        this.usedOverlay = overlay;

        if (overlay)
        {
            if ($(this.root).closest(".w-main").length > 0)
                $(this.root).closest(".w-main").addClass("is-blurred");
            else
                $(this.root).addClass("is-blurred");
        }

        if (!this.isVisible())
        {
            // Set current active element, used to reset focus after hiding.
            this.lastActiveElement = document.activeElement as HTMLElement;

            // Add ARIA labels
            this.root.setAttribute("tabindex", "-1");
            this.root.setAttribute("role", "alert");
            this.root.setAttribute("aria-live", "assertive");
            this.root.setAttribute("aria-modal", "true");
            this.root.removeAttribute("aria-hidden");

            this.root.classList.add("is-active");
            $("html").addClass("u-overlay-active");

            // Focus to spinner for screenreaders
            this.root.focus();

            (window as any).filteringInProgress = true;
        }
    }

    public hide()
    {
        $(".is-blurred").removeClass("is-blurred");
        $("html").removeClass("u-overlay-active");

        if (this.isVisible())
        {
            // hide aria labels
            this.root.setAttribute("aria-hidden", "true");
            this.root.removeAttribute("role");
            this.root.removeAttribute("aria-live");
            this.root.removeAttribute("aria-modal");

            this.root.classList.remove("is-active");

            (window as any).filteringInProgress = false;

            // Reset focus
            if (this.lastActiveElement)
            {
                this.lastActiveElement.focus();
            }
        }
    }

    private exists(): boolean
    {
        if (typeof this.input === "string" && document.querySelector(this.input + " .u-progress-spinner"))
            return true;
        else if ($("body").find(this.container)?.length > 0)
            return true;
        else
            return false;
    }

    public isVisible(): boolean
    {
        return $(this.root).hasClass("is-active");
    }
};

(window as any).progressSpinner = progressSpinner;